import React, { useState, useRef, useEffect } from "react";
import GeneratePDFButton from "./GeneratePDFButton";
import GemstoneProgress from "./GemstoneProgress";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { makeRequest } from "./../services/api";
import { webEndPoint } from "./../constant/constant";
import WarningModal from "./Modals/WarningModal";

export const formatDobInput = (value = "", prevValue = "") => {
  // Extract only digits from input
  const newDigits = value.replace(/\D/g, "").slice(0, 8);
  const prevDigits = prevValue.replace(/\D/g, "");

  if (!newDigits) return "";

  // If user is deleting (fewer digits)
  if (newDigits.length < prevDigits.length) {
    // Parse the structure of the raw input to preserve it
    const parts = value.split("/");

    if (parts.length === 1) {
      // Only day part
      return newDigits;
    } else if (parts.length === 2) {
      // Day and month parts
      const dayDigits = parts[0].replace(/\D/g, "");
      const monthDigits = parts[1].replace(/\D/g, "");
      return `${dayDigits}${monthDigits ? "/" + monthDigits : ""}`;
    } else {
      // All three parts
      const dayDigits = parts[0].replace(/\D/g, "");
      const monthDigits = parts[1].replace(/\D/g, "");
      const yearDigits = parts[2].replace(/\D/g, "");

      let result = dayDigits;
      if (monthDigits) result += "/" + monthDigits;
      if (yearDigits) result += "/" + yearDigits;
      return result;
    }
  }

  // Normal addition logic
  if (newDigits.length <= 2) return newDigits;
  if (newDigits.length <= 4) return `${newDigits.slice(0, 2)}/${newDigits.slice(2)}`;

  return `${newDigits.slice(0, 2)}/${newDigits.slice(2, 4)}/${newDigits.slice(4)}`;
};

export const parseDobString = (value = "") => {
  const match = /^\s*(\d{2})\/(\d{2})\/(\d{4})\s*$/.exec(value);

  if (!match) return "";

  const [, day, month, year] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));

  if (
    date.getFullYear() !== Number(year) ||
    date.getMonth() !== Number(month) - 1 ||
    date.getDate() !== Number(day)
  ) {
    return "";
  }

  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

export const formatTimeInput = (value = "", prevValue = "") => {
  // Extract only digits from input
  const newDigits = value.replace(/\D/g, "").slice(0, 4);
  const prevDigits = prevValue.replace(/\D/g, "");

  if (!newDigits) return "";

  // If user is deleting (fewer digits)
  if (newDigits.length < prevDigits.length) {
    if (newDigits.length <= 2) return newDigits;
    return `${newDigits.slice(0, 2)}:${newDigits.slice(2)}`;
  }

  // Normal addition logic
  if (newDigits.length <= 2) return newDigits;
  return `${newDigits.slice(0, 2)}:${newDigits.slice(2, 4)}`;
};

export const validateTimeString = (time, period) => {
  const match = /^\s*(\d{2}):(\d{2})\s*$/.exec(time);

  if (!match) {
    return "Please enter time in HH:MM format.";
  }

  const [, hours, minutes] = match;
  const hoursNum = Number(hours);
  const minutesNum = Number(minutes);

  // Check hours range
  if (hoursNum < 1 || hoursNum > 12) {
    return "Hours must be between 01 and 12.";
  }

  // Check minutes range
  if (minutesNum < 0 || minutesNum > 59) {
    return "Minutes must be between 00 and 59.";
  }

  return "";
};

export const convertTo24Hour = (timeString, period) => {
  const match = /^\s*(\d{2}):(\d{2})\s*$/.exec(timeString);

  if (!match) return { hour: 0, minute: 0 };

  let [, hours, minutes] = match;
  let hour = Number(hours);
  const minute = Number(minutes);

  // Convert 12-hour to 24-hour format
  if (period === "AM") {
    if (hour === 12) {
      hour = 0; // 12:xx AM is 00:xx in 24-hour
    }
  } else if (period === "PM") {
    if (hour !== 12) {
      hour = hour + 12; // Convert 1-11 PM to 13-23
    }
    // 12:xx PM stays as 12
  }

  return { hour, minute };
};

function BirthDetailsForm() {
  const [step, setStep] = useState("form");
  const [progress, setProgress] = useState(0);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [message, setMessage] = useState("");
  const [headerTitle, setHeaderTitle] = useState("");
  const [reportId, setReportId] = useState(null);

  const navigate = useNavigate();

  const progressIntervalRef = useRef(null);
  const delayTimeoutRef = useRef(null);
  const pollingRef = useRef(null);
  const prevDobRef = useRef("");
  const prevTimeRef = useRef("");

  const [form, setForm] = useState({
    name: "",
    dob: "",
    time: "",
    timePeriod: "AM",
    mobile: "",
    language: "",
    place: "",
    lat: null,
    lon: null,
    weight: "",
  });

  const [errors, setErrors] = useState({
    dob: "",
    time: "",
  });

  const getErrorMessage = (error) => {
    const payload = error?.response?.data || error?.detail || error;

    if (!payload) {
      return "Something Went Wrong, Please Try again Later...!";
    }

    if (Array.isArray(payload) && payload.length > 0) {
      const firstError = payload[0];
      if (firstError?.msg) {
        return firstError.msg;
      }
    }

    if (typeof payload === "string" && payload) {
      return payload.detail;
    }

    if (typeof payload.message === "string" && payload.message) {
      return payload.message;
    }

    return "Something Went Wrong, Please Try again Later...!";
  };

  /* -------- DOB VALIDATION FUNCTION -------- */
  const validateDob = (dobValue) => {
    const match = /^\s*(\d{2})\/(\d{2})\/(\d{4})\s*$/.exec(dobValue);

    if (!match) {
      return "Please enter date in dd/mm/yyyy format.";
    }

    const [, day, month, year] = match;
    const dayNum = Number(day);
    const monthNum = Number(month);
    const yearNum = Number(year);

    // Check day range
    if (dayNum < 1 || dayNum > 31) {
      return "Date must be between 01 and 31.";
    }

    // Check month range
    if (monthNum < 1 || monthNum > 12) {
      return "Month must be between 01 and 12.";
    }

    // Validate actual date (handles invalid dates like Feb 30)
    const dobDate = new Date(yearNum, monthNum - 1, dayNum);
    if (
      dobDate.getFullYear() !== yearNum ||
      dobDate.getMonth() !== monthNum - 1 ||
      dobDate.getDate() !== dayNum
    ) {
      return "Invalid date. Please check your entry.";
    }

    // Check if date is in future
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dobDate > today) {
      return "Date of birth cannot be in the future.";
    }

    return "";
  };

  /* -------- PROGRESS LOGIC -------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "weight") {
      if (value === "") {
        setForm((prev) => ({ ...prev, [name]: "" }));
        return;
      }
      if (Number(value) === 0) return;
      if (value.length > 3) return;
    }
    if (name === "mobile") {
      if (value.length > 10) return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDobChange = (e) => {
    const rawValue = e.target.value;
    const prevValue = prevDobRef.current;

    // Format with knowledge of previous value to handle deletion better
    const formattedValue = formatDobInput(rawValue, prevValue);

    prevDobRef.current = formattedValue;
    setForm((prev) => ({ ...prev, dob: formattedValue }));
    // Clear error when user starts typing
    setErrors((prev) => ({ ...prev, dob: "" }));
  };

  const handleTimeChange = (e) => {
    const rawValue = e.target.value;
    const prevValue = prevTimeRef.current;

    // Format with knowledge of previous value to handle deletion better
    const formattedValue = formatTimeInput(rawValue, prevValue);

    prevTimeRef.current = formattedValue;
    setForm((prev) => ({ ...prev, time: formattedValue }));
    // Clear error when user starts typing
    setErrors((prev) => ({ ...prev, time: "" }));
  };

  const handleTimePeriodChange = (option) => {
    setForm((prev) => ({ ...prev, timePeriod: option.value }));
  };
  const getLocality = (addressComponents) => {
    const localityObj = addressComponents?.find((comp) =>
      comp.types.includes("locality"),
    );

    return localityObj?.long_name || "";
  };

  const fetchPlaces = async (input) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          input,
        )}&key=AIzaSyAqRdpyrLVwenKukD1auTANi5I6C_UBNaM`,
      );

      const data = await response.json();

      const opts = (data.results || [])
        .map((item) => {
          const locality = getLocality(item.address_components);
          return {
            label: locality,
            value: locality,
            lat: item.geometry.location.lat,
            lon: item.geometry.location.lng,
          };
        })
        .filter((opt) => opt.label);

      setOptions(opts);
    } catch (err) {
      console.error(err);
      setOptions([]);
    }
  };

  const handleInputChange = (newInputValue, { action }) => {
    if (action === "input-change") {
      setInputValue(newInputValue);
      if (newInputValue.length > 2) fetchPlaces(newInputValue);
      else setOptions([]);
    }
    return newInputValue;
  };
  const handlePlaceChange = (selectedOption) => {
    if (selectedOption) {
      setForm((prev) => ({
        ...prev,
        place: selectedOption.value,
        lat: selectedOption.lat,
        lon: selectedOption.lon,
      }));
      setInputValue(selectedOption.label);
      setOptions([selectedOption]);
    } else {
      setForm((prev) => ({
        ...prev,
        place: "",
        lat: null,
        lon: null,
      }));
      setInputValue("");
      setOptions([]);
    }
  };
  const startFakeProgress = () => {
    setProgress(0);

    progressIntervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) return prev;
        const increment = Math.floor(Math.random() * 3) + 1;
        return Math.min(prev + increment, 90);
      });
    }, 1500);
  };

  const checkReportStatus = async (id) => {
    try {
      return await makeRequest("get", webEndPoint + `reports/${id}`);
    } catch {
      return null;
    }
  };

  const startPollingAfterOneMinute = (id) => {
    delayTimeoutRef.current = setTimeout(() => {
      startPolling(id);
    }, 60000);
  };

  const startPolling = (id) => {
    const poll = async () => {
      const res = await checkReportStatus(id);

      if (res?.status === "success" || res?.status === "completed") {
        handleSuccess(id);
        return;
      }

      if (res?.status === "duplicate") {
        stopAll();
        setOpen(true);
        setMessage(
          "Your mobile number has already been used to generate a Gems PDF. Please use a new mobile number to generate another PDF.",
        );
        setHeaderTitle("PDF Generation Limit Reached");
        setStep("form");
        return;
      }
      if (res?.status === "error") {
        stopAll();
        setOpen(true);
        setHeaderTitle("");
        setMessage("Server Error, Please Try again Later...!");
        setStep("form");
        return;
      }

      pollingRef.current = setTimeout(poll, 5000);
    };

    poll();
  };

  const handleSuccess = (id) => {
    stopAll();
    setProgress(100);

    setTimeout(() => {
      navigate(`/gemstone-report/${id}`, { replace: true });
    }, 15000);
  };

  const stopAll = () => {
    clearInterval(progressIntervalRef.current);
    clearTimeout(delayTimeoutRef.current);
    clearTimeout(pollingRef.current);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setErrors({ dob: "", time: "" });

    if (!/^\d{10}$/.test(form.mobile)) {
      alert("Please enter a valid 10-digit mobile number.");
      return;
    }

    // Validate DOB
    const dobError = validateDob(form.dob);
    if (dobError) {
      setErrors((prev) => ({ ...prev, dob: dobError }));
      return;
    }

    // Validate Time
    const timeError = validateTimeString(form.time, form.timePeriod);
    if (timeError) {
      setErrors((prev) => ({ ...prev, time: timeError }));
      return;
    }

    try {
      const dobIso = parseDobString(form.dob);
      const dobDate = new Date(`${dobIso}T00:00:00`);

      // Convert time from 12-hour to 24-hour format
      const { hour, minute } = convertTo24Hour(form.time, form.timePeriod);

      const payload = {
        name: form.name,
        gender: "Male",
        day: dobDate.getDate(),
        month: dobDate.getMonth() + 1,
        year: dobDate.getFullYear(),
        hour: hour,
        minute: minute,
        lat: form.lat,
        lon: form.lon,
        tzone: 5.5,
        place: form.place,
        phone: form.mobile,
        report_lang: form.language,
        jid: JSON.parse(localStorage.getItem("users") || "{}").id || "",
      };

      const res = await makeRequest(
        "post",
        webEndPoint + "generate_report",
        JSON.stringify(payload),
        { "Content-Type": "application/json" },
      );

      if (res?.report_id) {
        setStep("progress");
        startFakeProgress();
        startPollingAfterOneMinute(res.report_id);
      }
      if (res?.status === "duplicate") {
        setOpen(true);
        setMessage(
          "Your mobile number has already been used to generate a Gems PDF. Please use a new mobile number to generate another PDF.",
        );
        setHeaderTitle("PDF Generation Limit Reached");
        setStep("form");
        return;
      }
      if (res?.status === "processing") {
        setOpen(true);
        setMessage(res?.message);
        setHeaderTitle("PDF Generation in Progress");
        setStep("form");
        return;
      }
    } catch (err) {
      const apiErrorMessage = getErrorMessage(err);
      setOpen(true);
      setMessage(apiErrorMessage);
      setHeaderTitle("");
    }
  };
  useEffect(() => {
    return () => stopAll();
  }, []);

  if (step === "progress") {
    return <GemstoneProgress progress={progress} />;
  }
  const languageOptions = [
    { label: "English", value: "english" },
    { label: "Tamil", value: "tamil" },
  ];
  return (
    <section className="form-section">
      <div className="form-card">
        <h2 className="form-title">Provide your birth details to continue.</h2>
        <form className="birth-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <div className="fullname-input-container">
                <label className="fullname-label">
                  Full Name <span className="fullname-required">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter your fullname"
                  value={form.name}
                  required
                  autoComplete="off"
                  onChange={handleChange}
                  className="fullname-input"
                />
              </div>
            </div>
            <div className="form-group">
              <div className="fullname-input-container">
                <label className="fullname-label">
                  Date of Birth <span className="fullname-required">*</span>
                </label>
                <input
                  type="text"
                  name="dob"
                  required
                  className={`fullname-input ${errors.dob ? "error" : ""}`}
                  value={form.dob}
                  onChange={handleDobChange}
                  placeholder="dd/mm/yyyy"
                  maxLength={10}
                  inputMode="numeric"
                  pattern="\d{2}/\d{2}/\d{4}"
                  autoComplete="off"
                  onKeyDown={(event) => {
                    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                      return;
                    }
                  }}
                />
                {errors.dob && (
                  <span className="error-message" style={{ color: "#ff6b6b", fontSize: "12px", marginTop: "5px", display: "block" }}>
                    {errors.dob}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group time-input-group">
              <div className="fullname-input-container">
                <label className="fullname-label">
                  Time of Birth <span className="fullname-required">*</span>
                </label>
                <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      name="time"
                      required
                      className={`fullname-input ${errors.time ? "error" : ""}`}
                      value={form.time}
                      onChange={handleTimeChange}
                      placeholder="HH:MM"
                      maxLength={5}
                      inputMode="numeric"
                      pattern="\d{2}:\d{2}"
                      autoComplete="off"
                    />
                    {errors.time && (
                      <span className="error-message" style={{ color: "#ff6b6b", fontSize: "12px", marginTop: "5px", display: "block" }}>
                        {errors.time}
                      </span>
                    )}
                  </div>
                  <Select
                    name="timePeriod"
                    options={[
                      { label: "AM", value: "AM" },
                      { label: "PM", value: "PM" },
                    ]}
                    value={{ label: form.timePeriod, value: form.timePeriod }}
                    onChange={handleTimePeriodChange}
                    isSearchable={false}
                    required
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: "#0c0324",
                        height: 47,
                        borderColor: base.isFocused ? "#D7CDCD" : "#828282",
                        color: "#D7CDCD",
                        fontWeight: "300",
                        fontSize: "12px",
                        boxShadow: "none",
                        minWidth: "100px",
                        "&:hover": {
                          border: "1px solid #797688",
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "#D7CDCD",
                        fontWeight: "300",
                        fontSize: "12px",
                      }),
                      input: (base) => ({
                        ...base,
                        color: "#D7CDCD",
                        fontWeight: "300",
                        fontSize: "12px",
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="form-group place-birth" style={{ minWidth: 240 }}>
              <label className="place-birth-label">
                Place of Birth <span className="fullname-required">*</span>
              </label>
              <Select
                className="select-input"
                name="place"
                options={options}
                onInputChange={handleInputChange}
                onChange={handlePlaceChange}
                value={
                  form.place
                    ? options.find((opt) => opt.value === form.place) || null
                    : null
                }
                placeholder="Search your Birth Place"
                isClearable
                menuPosition="fixed"
                menuPortalTarget={document.body} // ✅ IMPORTANT
                required
                styles={{
                  control: (base) => ({
                    ...base,
                    backgroundColor: "#0c0324",
                    height: 47,
                    borderColor: base.isFocused ? "#D7CDCD" : "#828282",
                    color: "#D7CDCD",
                    fontWeight: "300",
                    fontSize: "12px",
                    boxShadow: "none",
                    "&:hover": {
                      border: "1px solid #797688",
                    },
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: "#D7CDCD",
                    fontWeight: "300",
                    fontSize: "12px",
                  }),
                  input: (base) => ({
                    ...base,
                    color: "#D7CDCD",
                    fontWeight: "300",
                    fontSize: "12px",
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: "#D7CDCD",
                    fontWeight: "300",
                    opacity: "0.7",
                    fontSize: "12px",
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <div className="fullname-input-container">
                <label className="fullname-label">
                  Whatsapp Mobile Number{" "}
                  <span className="fullname-required">*</span>
                </label>
                <input
                  type="number"
                  name="mobile"
                  pattern="[0-9]{10}"
                  maxLength={10}
                  className="fullname-input"
                  required
                  autoComplete="off"
                  placeholder="Enter your Whatsapp mobile number"
                  value={form.mobile}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group place-birth" style={{ minWidth: 240 }}>
              <label className="place-birth-label">
                Select Language <span className="fullname-required">*</span>
              </label>
              <Select
                name="language"
                options={languageOptions}
                value={languageOptions.find(
                  (opt) => opt.value === form.language,
                )}
                onChange={(opt) =>
                  setForm((prev) => ({ ...prev, language: opt.value }))
                }
                placeholder="Select Language"
                required
                menuPosition="fixed"
                menuPortalTarget={document.body} // ✅ IMPORTANT
                styles={{
                  control: (base) => ({
                    ...base,
                    backgroundColor: "#0c0324",
                    height: 47,
                    borderColor: base.isFocused ? "#D7CDCD" : "#828282",
                    color: "#D7CDCD",
                    fontWeight: "300",
                    fontSize: "12px",
                    boxShadow: "none",
                    "&:hover": {
                      border: "1px solid #797688",
                    },
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: "#D7CDCD",
                    fontWeight: "300",
                    fontSize: "12px",
                  }),
                  input: (base) => ({
                    ...base,
                    color: "#D7CDCD",
                    fontWeight: "300",
                    fontSize: "12px",
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: "#D7CDCD",
                    fontWeight: "300",
                    fontSize: "12px",
                    opacity: "0.7",
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <div className="fullname-input-container">
                <label className="fullname-label">
                  Current Weight in (kg){" "}
                  <span className="fullname-required">*</span>
                </label>
                <input
                  type="number"
                  name="weight"
                  placeholder="Enter your Current weight in (kg)"
                  value={form.weight}
                  required
                  maxLength="3"
                  autoComplete="off"
                  onChange={handleChange}
                  className="fullname-input"
                />
              </div>
            </div>
          </div>
          <div className="mandatory-note">
            <input
              className="checkmark"
              type="checkbox"
              checked
              readOnly
              tabIndex={-1}
            />
            <span>All Fields are mandatory</span>
          </div>
          <GeneratePDFButton />
        </form>
      </div>
      <WarningModal
        open={open}
        onClose={() => setOpen(false)}
        message={message}
        headerTitle={headerTitle}
      />
    </section>
  );
}

export default BirthDetailsForm;
